import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";


const DISCOUNT_SORT_FIELDS = {
  ID: "ID",
  DiscountName: "DiscountName",
  OrderAmount: "OrderAmount",
};

const DISCOUNT_SORT_LABELS = {
  [DISCOUNT_SORT_FIELDS.ID]: "ID",
  [DISCOUNT_SORT_FIELDS.DiscountName]: "Name",
  [DISCOUNT_SORT_FIELDS.OrderAmount]: "Order Amount",
};

const ManageDiscount = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const discountsFetchSeqRef = useRef(0);

  const pageKey = "discounts";
  const defaultVisibleColumns = {
    discountName: true,
    discountPercentage: true,
    maxDiscountValue: true,
    rule: true,
    minimumQuantity: true,
    minimumSpend: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const SHOW_BULK_ACTIONS = false;

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (currencyCode) => currencySymbols[currencyCode] || currencyCode;

  const navigate = useNavigate();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: DISCOUNT_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const fetchDiscounts = useCallback(async () => {
    const fetchSeq = ++discountsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DISCOUNTS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      if (fetchSeq !== discountsFetchSeqRef.current) return;

      const discountData = response.data.data || response.data;
      const list = Array.isArray(discountData) ? discountData : [];
      setDiscounts(list);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, list.length);
      }
    } catch (err) {
      if (fetchSeq !== discountsFetchSeqRef.current) return;
      setDiscounts([]);
      if (err.response?.status !== 404 && !search.trim()) {
        showErrorToast("Failed to load discounts.");
      }
    } finally {
      if (fetchSeq === discountsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID]);

  const totalRecords = discounts.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedDiscounts = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return discounts.slice(start, start + size);
  }, [discounts, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(discountsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchDiscounts(), [fetchDiscounts]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const hasFiltersApplied = () => search.trim() !== "";

  const showEmptyCatalogState =
    initialLoadComplete && !loading && !hasFiltersApplied() && catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No discounts match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no discounts to show right now.";
  };

  const renderNoDataMessage = () => {
    if (loading || discounts.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No discounts found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const handleAddDiscount = () => navigate("/discount/adddiscount");

  const handleDeleteDiscount = async (discountItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete discount ${discountItem.discountName}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!discountItem.id || !CompID) {
        showErrorToast("Invalid discount or company ID.");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_DISCOUNT, {
        params: {
          CompID,
          id: discountItem.id,
        },
      });

      if (response.data.status) {
        setDiscounts((prevDiscounts) => {
          const next = prevDiscounts.filter((item) => item.id !== discountItem.id);
          if (!search.trim()) {
            applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          }
          return next;
        });
        showSuccessToast("Discount deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the discount.");
      setLoading(false);
    }
  };

  const handleSelectItem = (discountId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (pagedDiscounts[i]?.id) {
            newSelected.add(pagedDiscounts[i].id);
          }
        }
      } else {
        if (newSelected.has(discountId)) {
          newSelected.delete(discountId);
        } else {
          newSelected.add(discountId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(pagedDiscounts.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No discounts selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected discounts?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((discountId) =>
          axiosInstance.delete(APIs.DELETE_DISCOUNT, {
            params: {
              CompID,
              id: discountId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchDiscounts();
      showSuccessToast(`${selectedItems.size} discounts deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected discounts.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No discounts selected for editing.");
      return;
    }

    const selectedDiscountIds = Array.from(selectedItems);
    navigate(`/discount/bulkEdit`, { state: { selectedDiscountIds } });
  };

  const navigateToDiscount = (id) => navigate(`/discount/updateDiscount/${id}`);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Discount</li>
          </ol>
          <h1 className="page-header mb-0">Manage Discount</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={handleAddDiscount}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Discount
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddDiscount} title="Add Discount" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Discounts"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by discount name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} discount
                        {selectedItems.size > 1 ? "s" : ""} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                      >
                        <i className="fa fa-trash me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedDiscounts.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  pagedDiscounts.length > 0 &&
                                  selectedItems.size === pagedDiscounts.length
                                }
                              />
                            </th>
                          )}
                          {visibleColumns.discountName && (
                            <SortableColumnHeader
                              sortField={DISCOUNT_SORT_FIELDS.DiscountName}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.discountPercentage && (
                            <SortableColumnHeader
                              sortField={DISCOUNT_SORT_FIELDS.ID}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Discount %
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.maxDiscountValue && (
                            <th className="pt-2 pb-2 text-center">Max Discount Value</th>
                          )}
                          {visibleColumns.rule && (
                            <th className="pt-2 pb-2 text-start">Rule</th>
                          )}
                          {visibleColumns.minimumQuantity && (
                            <th className="pt-2 pb-2 text-center">Min. Quantity</th>
                          )}
                          {visibleColumns.minimumSpend && (
                            <th className="pt-2 pb-2 text-center">Min. Spend</th>
                          )}
                          <th className="pt-2 pb-2 text-start">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedDiscounts.map((discount, index) => (
                          <tr key={discount.id}>
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(discount.id)}
                                  onChange={(e) =>
                                    handleSelectItem(discount.id, index, e)
                                  }
                                />
                              </td>
                            )}
                            {visibleColumns.discountName && (
                              <td
                                className="align-middle text-start"
                                onClick={() => navigateToDiscount(discount.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {discount.discountName}
                              </td>
                            )}
                            {visibleColumns.discountPercentage && (
                              <td
                                className="align-middle text-center"
                                onClick={() => navigateToDiscount(discount.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {discount.discountPercentage ?? discount.DiscountPercentage ?? 0}%
                              </td>
                            )}
                            {visibleColumns.maxDiscountValue && (
                              <td
                                className="align-middle text-center"
                                onClick={() => navigateToDiscount(discount.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {(discount.maxDiscountValue != null &&
                                  discount.maxDiscountValue !== "") ||
                                (discount.MaxDiscountValue != null &&
                                  discount.MaxDiscountValue !== "")
                                  ? `${getCurrencySymbol(compCurrency)}${Number(
                                      discount.maxDiscountValue ?? discount.MaxDiscountValue ?? 0
                                    ).toFixed(2)}`
                                  : "—"}
                              </td>
                            )}
                            {visibleColumns.rule && (
                              <td
                                className="align-middle text-start"
                                onClick={() => navigateToDiscount(discount.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {(() => {
                                  const ruleType =
                                    discount.discountRuleType ||
                                    discount.DiscountRuleType ||
                                    "—";
                                  if (ruleType !== "Order count") return ruleType;
                                  const compare =
                                    discount.orderCountCompareType ||
                                    discount.OrderCountCompareType ||
                                    "Equals";
                                  const threshold =
                                    discount.orderCountThreshold ??
                                    discount.OrderCountThreshold ??
                                    0;
                                  const compareLabel =
                                    compare === "AtMost" ? "At most" : "Equals";
                                  return `${ruleType} (${compareLabel} ${threshold})`;
                                })()}
                              </td>
                            )}
                            {visibleColumns.minimumQuantity && (
                              <td
                                className="align-middle text-center"
                                onClick={() => navigateToDiscount(discount.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {discount.discountApplyWhenQTYofItems ??
                                  discount.DiscountApplyWhenQTYofItems ??
                                  0}
                              </td>
                            )}
                            {visibleColumns.minimumSpend && (
                              <td
                                className="align-middle text-center"
                                onClick={() => navigateToDiscount(discount.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {(discount.isSetMinimumSpend || discount.IsSetMinimumSpend) &&
                                (discount.minimumSpendAmount != null ||
                                  discount.MinimumSpendAmount != null)
                                  ? `${getCurrencySymbol(compCurrency)}${Number(
                                      discount.minimumSpendAmount ??
                                        discount.MinimumSpendAmount ??
                                        0
                                    ).toFixed(2)}`
                                  : "—"}
                              </td>
                            )}
                            <td className="align-middle text-start">
                              <TableActionsMenu dropup menuStyle={{ zIndex: 9999 }}>
                                <TableActionsItem
                                  as={Link}
                                  to={`/discount/updateDiscount/${discount.id}`}
                                  icon="fa fa-edit"
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteDiscount(discount)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
};

export default ManageDiscount;
